import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { ConfigProvider, Spin } from "antd";
import antdPtPT from "antd/es/locale/pt_PT";
import { useTranslation } from "react-i18next";
import { ConsentProvider } from "@salespark/consent-gate/react";
import { ConsentModal } from "@salespark/consent-gate/ui";
import { gate } from "./utils/consent";
import { scripts, vendors } from "./consent-scripts";

// Eager: the primary checkout route and the small shared error screens (also used
// inline by IndexPage, so they stay in the main bundle).
import IndexPage from "./pages";
import ProductNotFound from "./Components/Shared/ProductNotFound";
import InvalidCountry from "./Components/Shared/InvalidCountry";
import ProductSalesClosed from "./Components/Shared/ProductSalesClosed";
import UserBlocked from "Components/Shared/UserBlocked";

// Lazy: heavy secondary routes, split into their own chunks so the checkout no
// longer downloads billing / tickets / confirm / cofidis / installments / forms code.
const BillingInformation = lazy(() => import("./pages/billing-information"));
const Authentication = lazy(() => import("./pages/authentication"));
const ConfirmPayment = lazy(() => import("./pages/confirm-payment"));
const CofidisPayPayment = lazy(() => import("./pages/cofidispay-payment"));
const PurchaseCompleted = lazy(() => import("pages/purchase-completed"));
const InstallmentsPayment = lazy(() => import("pages/installments-payment"));
const AttendeeTicket = lazy(() => import("pages/tickets"));
const IMMForm = lazy(() => import("./pages/forms/IGBN1MPO3NYMK"));

const COOKIE_POLICY_URL = "https://ajuda.salespark.io/help/politica-de-cookies";

const RouteFallback = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
    <Spin size="large" />
  </div>
);

function App() {
  const { i18n } = useTranslation();
  const locale = i18n?.language?.toLowerCase().startsWith("en") ? "en" : "pt";

  return (
    <ConsentProvider gate={gate} scripts={scripts}>
      <ConfigProvider
        locale={antdPtPT}
        areaMapper={(area) => {
          return {
            ...area,
            emoji: <span className={`fp ${area.short.toLowerCase()}`} />,
          };
        }}
      >
        <Router>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route exact path="/authentication" element={<Authentication />} />
              <Route exact path="/:product_id/purchase-completed" element={<PurchaseCompleted />} />
              <Route exact path="/billing/:purchase_id" element={<BillingInformation />} />
              <Route exact path="/product-sales-closed" element={<ProductSalesClosed />} />
              <Route exact path="/product-not-found" element={<ProductNotFound />} />
              <Route exact path="/invalid-country" element={<InvalidCountry />} />
              <Route exact path="/something-went-wrong" element={<UserBlocked />} />
              <Route exact path="/installments-payment/:purchase_id" element={<InstallmentsPayment />} />
              <Route exact path="/forms/IGBN1MPO3NYMK" element={<IMMForm />} />
              <Route exact path="/:product_id" element={<IndexPage />} />
              <Route exact path="/ticket/:ticket_code" element={<AttendeeTicket />} />
              <Route
                exact
                path="/:product_id/confirm-payment/:purchase_id/:payment_unique_id?"
                element={<ConfirmPayment />}
              />
              <Route
                exact
                path="/:product_id/cofidispay-payment/:purchase_id/:payment_unique_id"
                element={<CofidisPayPayment />}
              />
              <Route exact path="/" element={<IndexPage />} />
            </Routes>
          </Suspense>
        </Router>
      </ConfigProvider>

      <ConsentModal
        layout="bar"
        preset="checkout"
        locale={locale}
        vendors={vendors}
        legal={{ privacyUrl: COOKIE_POLICY_URL, cookieUrl: COOKIE_POLICY_URL }}
      />
    </ConsentProvider>
  );
}

export default App;
